import Avatar from '@mui/material/Avatar';
import PersonIcon from '@mui/icons-material/Person';
import { alpha, useTheme } from '@mui/material/styles';

function stringToColor(value: string): string {
  let hash = 0;
  for (let i = 0; i < value.length; i++) hash = value.charCodeAt(i) + ((hash << 5) - hash);
  let color = '#';
  for (let i = 0; i < 3; i++) {
    const byte = (hash >> (i * 8)) & 255;
    color += `00${byte.toString(16)}`.slice(-2);
  }
  return color;
}

function initialsFor(label: string): string {
  const words = label
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return '';
  if (words.length === 1) return words[0].charAt(0).toUpperCase();
  return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
}

/**
 * Fallback hierarchy: 1) the remote image (browser handles broken-image
 * fallback to children automatically via MUI Avatar's onError), 2) initials
 * from displayName or handle, 3) a generic person icon. Used consistently
 * everywhere a profile picture is shown so the fallback behavior never
 * drifts between call sites.
 *
 * `genericFallback` skips step 2 entirely (no initials, ever) — for analyzed
 * evidence images, where a colorful initials avatar could be mistaken for
 * real evidence rather than a plain "unavailable" placeholder.
 */
export const ProfileImage = ({
  imageUrl,
  displayName,
  handle,
  size = 40,
  genericFallback = false,
}: {
  imageUrl: string | null;
  displayName: string | null;
  handle?: string | null;
  size?: number;
  genericFallback?: boolean;
}) => {
  const theme = useTheme();
  const label = genericFallback ? null : displayName ?? handle ?? null;
  const initials = label ? initialsFor(label) : '';

  return (
    <Avatar
      src={imageUrl ?? undefined}
      sx={{
        width: size,
        height: size,
        fontSize: Math.max(10, Math.round(size * 0.38)),
        fontWeight: 700,
        bgcolor: label ? alpha(stringToColor(label), 0.85) : theme.palette.action.selected,
        color: label ? theme.palette.common.white : theme.palette.text.secondary,
      }}
    >
      {initials || <PersonIcon sx={{ fontSize: Math.round(size * 0.6) }} />}
    </Avatar>
  );
};
